import * as esbuild from 'esbuild-wasm';
import type { FireShader } from './effects/fire';
import type { SlimeballShader } from './effects/slimeball';
import type { FireballShader } from './effects/fireball';
import type { WaterWaveShader } from './simulations/waterwave';
import type { SnowfallShader } from './simulations/snowfall';
import type { SpinningCubeShader } from './objects/spinningcube';
import { hexToVec3 } from './utils';
import { ShaderBehavior } from './shader-types';

type ShaderModel =
  | FireShader
  | SlimeballShader
  | WaterWaveShader
  | SnowfallShader
  | FireballShader
  | SpinningCubeShader;

let esbuildInitialized = false;

// Generate dynamic uniform setup based on shader model properties
function generateUniformSetup(shaderModel: ShaderModel): string {
  const uniforms: string[] = [];

  // Duration uniform removed - using continuous time for natural effects

  // Shader-specific uniforms
  if ('volatility' in shaderModel) {
    uniforms.push(
      `  const u_volatility = gl.getUniformLocation(program, 'u_volatility');`
    );
    uniforms.push(`  gl.uniform1f(u_volatility, ${shaderModel.volatility});`);
  }

  if ('baseColor' in shaderModel) {
    uniforms.push(
      `  const u_base_color = gl.getUniformLocation(program, 'u_base_color');`
    );
    uniforms.push(
      `  gl.uniform3fv(u_base_color, ${JSON.stringify(
        hexToVec3(shaderModel.baseColor)
      )});`
    );
  }

  if ('topColor' in shaderModel) {
    uniforms.push(
      `  const u_top_color = gl.getUniformLocation(program, 'u_top_color');`
    );
    uniforms.push(
      `  gl.uniform3fv(u_top_color, ${JSON.stringify(
        hexToVec3(shaderModel.topColor)
      )});`
    );
  }

  if ('backgroundColor' in shaderModel) {
    uniforms.push(
      `  const u_background_color = gl.getUniformLocation(program, 'u_background_color');`
    );
    uniforms.push(
      `  gl.uniform3fv(u_background_color, ${JSON.stringify(
        hexToVec3(shaderModel.backgroundColor)
      )});`
    );
  }

  if ('color' in shaderModel) {
    uniforms.push(
      `  const u_color = gl.getUniformLocation(program, 'u_color');`
    );
    uniforms.push(
      `  gl.uniform3fv(u_color, ${JSON.stringify(
        hexToVec3(shaderModel.color)
      )});`
    );
  }

  if ('density' in shaderModel) {
    uniforms.push(
      `  const u_density = gl.getUniformLocation(program, 'u_density');`
    );
    uniforms.push(`  gl.uniform1f(u_density, ${shaderModel.density});`);
  }

  if ('speed' in shaderModel) {
    uniforms.push(
      `  const u_speed = gl.getUniformLocation(program, 'u_speed');`
    );
    uniforms.push(`  gl.uniform1f(u_speed, ${shaderModel.speed});`);
  }

  if ('amplitude' in shaderModel) {
    uniforms.push(
      `  const u_amplitude = gl.getUniformLocation(program, 'u_amplitude');`
    );
    uniforms.push(`  gl.uniform1f(u_amplitude, ${shaderModel.amplitude});`);
  }

  if ('frequency' in shaderModel) {
    uniforms.push(
      `  const u_frequency = gl.getUniformLocation(program, 'u_frequency');`
    );
    uniforms.push(`  gl.uniform1f(u_frequency, ${shaderModel.frequency});`);
  }

  if ('steepness' in shaderModel) {
    uniforms.push(
      `  const u_steepness = gl.getUniformLocation(program, 'u_steepness');`
    );
    uniforms.push(`  gl.uniform1f(u_steepness, ${shaderModel.steepness});`);
  }

  if ('intensity' in shaderModel) {
    uniforms.push(
      `  const u_intensity = gl.getUniformLocation(program, 'u_intensity');`
    );
    uniforms.push(`  gl.uniform1f(u_intensity, ${shaderModel.intensity});`);
  }

  if ('morph' in shaderModel) {
    uniforms.push(
      `  const u_morph = gl.getUniformLocation(program, 'u_morph');`
    );
    uniforms.push(`  gl.uniform1f(u_morph, ${shaderModel.morph});`);
  }

  if ('slime' in shaderModel) {
    uniforms.push(
      `  const u_slime = gl.getUniformLocation(program, 'u_slime');`
    );
    uniforms.push(`  gl.uniform1f(u_slime, ${shaderModel.slime});`);
  }

  if ('rotationSpeed' in shaderModel) {
    uniforms.push(
      `  const u_rotation_speed = gl.getUniformLocation(program, 'u_rotation_speed');`
    );
    uniforms.push(
      `  gl.uniform1f(u_rotation_speed, ${shaderModel.rotationSpeed});`
    );
  }

  if ('cubeColor' in shaderModel) {
    uniforms.push(
      `  const u_cube_color = gl.getUniformLocation(program, 'u_cube_color');`
    );
    uniforms.push(
      `  gl.uniform3fv(u_cube_color, ${JSON.stringify(
        hexToVec3(shaderModel.cubeColor)
      )});`
    );
  }

  return uniforms.join('\n');
}

export interface ExportData {
  html: string;
  javascript: string;
}

export async function generateExportCode(
  shaderModel: ShaderModel
): Promise<ExportData> {
  if (!esbuildInitialized) {
    await esbuild.initialize({
      wasmURL: 'https://unpkg.com/esbuild-wasm@0.25.9/esbuild.wasm',
    });
    esbuildInitialized = true;
  }

  // Use the shader's configured width and height
  const canvasWidth = shaderModel.width;
  const canvasHeight = shaderModel.height;

  const canvasId = `shader_${Math.random().toString(36).substr(2, 9)}`;

  const fragmentShader = shaderModel.generate();
  const vertexShader = `
    attribute vec2 a_position;
    void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
    }
  `;

  const uniformSetup = generateUniformSetup(shaderModel);

  const jsCode = `(() => {
  const canvas = document.getElementById('${canvasId}');
  if (!canvas) {
    console.error('Canvas element not found');
    return;
  }

  const gl = canvas.getContext('webgl');
  if (!gl) {
    console.error('WebGL not supported');
    return;
  }

  const vertexShaderSource = \`${vertexShader}\`;
  const fragmentShaderSource = \`${fragmentShader}\`;

  function createShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Error compiling shader:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);

  if (!vertexShader || !fragmentShader) {
    console.error('Failed to create shaders');
    return;
  }

  const program = gl.createProgram();
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Error linking program:', gl.getProgramInfoLog(program));
    return;
  }

  gl.useProgram(program);

  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  const positions = [-1, -1, 1, -1, -1, 1, 1, 1];
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);

  const positionAttributeLocation = gl.getAttribLocation(program, 'a_position');
  gl.enableVertexAttribArray(positionAttributeLocation);
  gl.vertexAttribPointer(positionAttributeLocation, 2, gl.FLOAT, false, 0, 0);

  const resolutionUniformLocation = gl.getUniformLocation(program, 'u_resolution');
  gl.uniform2f(resolutionUniformLocation, ${canvasWidth}, ${canvasHeight});

  // Only add reference resolution for texture shaders
  const shaderConstructor = shaderModel.constructor as any;
  const metadata = shaderConstructor.getMetadata ? shaderConstructor.getMetadata() : null;
  if (metadata && metadata.behavior === '${ShaderBehavior.TEXTURE}') {
    const referenceResolutionUniformLocation = gl.getUniformLocation(program, 'u_reference_resolution');
    gl.uniform2f(referenceResolutionUniformLocation, ${
      shaderModel.referenceWidth || canvasWidth
    }, ${shaderModel.referenceHeight || canvasHeight});
  }

  const timeUniformLocation = gl.getUniformLocation(program, 'u_time');

${uniformSetup}

  const startTime = Date.now();
  function render() {
    const elapsedTime = (Date.now() - startTime) / 1000;
    gl.uniform1f(timeUniformLocation, elapsedTime);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    requestAnimationFrame(render);
  }
  render();
})();`;

  // Temporarily disable esbuild minification due to truncation issue
  // TODO: Investigate esbuild truncation issue
  const html = `<canvas id="${canvasId}" width="${canvasWidth}" height="${canvasHeight}" style="max-width: 100%; height: auto; display: block;"></canvas>`;

  return {
    html,
    javascript: jsCode.trim(), // Trim whitespace and return raw code
  };
}
